import { Children, isValidElement, useEffect } from 'react';

const DEFAULT_TITLE_SUFFIX = `Vera`;

const META_ATTRIBUTES_MAP = {
  content: 'content',
  httpEquiv: 'http-equiv',
  media: 'media',
  name: 'name',
} as const;

const SPECIAL_NAMES = {
  THEME_COLOR: 'theme-color',
} as const;

const SUPPORTED_HTTP_HEADERS = {
  CONTENT_LANGUAGE: 'content-language',
  CONTENT_SECURITY_POLICY: 'content-security-policy',
  CONTENT_TYPE: 'content-type',
  DEFAULT_STYLE: 'default-style',
  REFRESH: 'refresh',
} as const;

type HTTPHeader = (typeof SUPPORTED_HTTP_HEADERS)[keyof typeof SUPPORTED_HTTP_HEADERS];

type MetaAttribute = keyof typeof META_ATTRIBUTES_MAP;

type MetaBaseConfig = { content: string; name: string };

type MetaHeaderConfig = { content: string; httpEquiv: HTTPHeader };

type MetaThemeConfig = { content: string; name: typeof SPECIAL_NAMES.THEME_COLOR; media?: string };

type Meta = MetaBaseConfig | MetaHeaderConfig | MetaThemeConfig;

const TAGS = {
  META: 'meta',
  TITLE: 'title',
};

const originalTitle = document.title;

const hasProp = <TProps, TKey extends string>(
  props: TProps,
  key: TKey,
): props is TProps & { [Key in TKey]: unknown } =>
  typeof props === 'object' && !!props && Object.hasOwn(props, key);

const isSupportedMetaAttribute = (name: string): name is MetaAttribute =>
  Object.hasOwn(META_ATTRIBUTES_MAP, name);

const hasStringProp = <TProps, TKey extends string>(
  props: TProps,
  key: TKey,
): props is TProps & { [Key in TKey]: string } =>
  hasProp(props, key) && typeof props[key] === 'string' && !!props[key];

const hasContent = <T,>(props: T): props is T & { content: string } =>
  hasStringProp(props, 'content');

const hasName = <T,>(props: T): props is T & { name: string } => hasStringProp(props, 'name');

const isMetaBase = (props: unknown): props is MetaBaseConfig => hasContent(props) && hasName(props);

const isMetaHeader = (props: unknown): props is MetaHeaderConfig =>
  hasContent(props) &&
  hasStringProp(props, 'httpEquiv') &&
  Object.values(SUPPORTED_HTTP_HEADERS).some((header) => header === props.httpEquiv);

const isMetaTheme = (props: unknown): props is MetaThemeConfig =>
  hasContent(props) && hasName(props) && props.name === SPECIAL_NAMES.THEME_COLOR;

const isMeta = (props: unknown): props is Meta =>
  isMetaBase(props) || isMetaHeader(props) || isMetaTheme(props);

const findMetaEl = (meta: Meta) => {
  const query = isMetaHeader(meta)
    ? `${META_ATTRIBUTES_MAP.httpEquiv}=${meta.httpEquiv}`
    : `${META_ATTRIBUTES_MAP.name}=${meta.name}`;

  return document.head.querySelector<HTMLMetaElement>(`meta[${query}]`);
};

const addMetaToHead = (meta: Meta, beforeModifyExistingEl: (key: string, node: Node) => void) => {
  const copyAttributes = (el: HTMLMetaElement) =>
    Object.entries(meta).forEach(([attribute, value]) => {
      if (!isSupportedMetaAttribute(attribute)) return;

      el.setAttribute(META_ATTRIBUTES_MAP[attribute], value);
    });

  const existingEl = findMetaEl(meta);

  if (existingEl) {
    beforeModifyExistingEl(existingEl.name || existingEl.httpEquiv, existingEl.cloneNode(true));
    copyAttributes(existingEl);
  } else {
    const newEl = document.createElement('meta');

    copyAttributes(newEl);
    document.head.appendChild(newEl);
  }
};

const removeMetaFromHead = (meta: Meta, originalNode?: Node) => {
  const el = findMetaEl(meta);

  if (!el) return;

  if (originalNode) document.head.replaceChild(originalNode, el);
  else document.head.removeChild(el);
};

type Props = React.PropsWithChildren<{
  meta?: Meta[];
  title?: string;
  titleSuffix?: string;
}>;

const Meta: React.FC<Props> = ({ children, titleSuffix = DEFAULT_TITLE_SUFFIX, ...metaProps }) => {
  useEffect(() => {
    const modifiedPreexistingNodes = new Map<string, Node>();

    if (metaProps.title)
      document.title = titleSuffix ? `${metaProps.title} - ${titleSuffix}` : metaProps.title;

    metaProps.meta?.forEach((meta) =>
      addMetaToHead(meta, (key, el) => modifiedPreexistingNodes.set(key, el)),
    );

    return () => {
      document.title = originalTitle;

      metaProps.meta?.forEach((meta) => {
        const mapKey = isMetaHeader(meta) ? meta.httpEquiv : meta.name;
        const originalEl = modifiedPreexistingNodes.get(mapKey);

        removeMetaFromHead(meta, originalEl);
      });
    };
  }, [metaProps.meta, metaProps.title, titleSuffix]);

  if (!children) return null;

  const parsedChildrenProps = { meta: metaProps.meta ?? [], title: metaProps.title };

  Children.toArray(children).forEach((child) => {
    if (!isValidElement(child)) return;

    if (child.type === TAGS.META && isMeta(child.props)) parsedChildrenProps.meta.push(child.props);
    else if (child.type === TAGS.TITLE && hasStringProp(child.props, 'children'))
      parsedChildrenProps.title = child.props.children;
  });

  return <Meta {...parsedChildrenProps} />;
};

export default Meta;
